import type { ActionDispatch, ComponentProps } from 'react';

import { useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Table } from 'rsuite';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

const { HeaderCell } = Table;

export type HeaderCellDropFn = (
  sourceAccessor: string,
  targetAccessor: string,
) => void;

export type HeaderCellDragState = {
  accessor: string;
  isDragging: boolean;
};

interface HeaderCellProps extends Omit<
  ComponentProps<typeof HeaderCell>,
  'onDrop'
> {
  accessor: string;
  onDrop?: HeaderCellDropFn;
  notDraggable?: boolean;
  onDraggingStateChange?: ActionDispatch<[HeaderCellDragState]>;
  hasFilters?: boolean;
  testId?: string;
}

const DraggableHeaderCell = ({
  children,
  onDrop,
  accessor,
  notDraggable,
  testId,
  onDraggingStateChange,
  hasFilters,
  ...rest
}: HeaderCellProps) => {
  const [, drop] = useDrop<{ accessor: string }>({
    accept: 'column',
    canDrop: () => !notDraggable,
    hover: (item, monitor) => {
      if (monitor.canDrop() && !monitor.didDrop() && !monitor.isOver()) {
        onDrop?.(item.accessor, accessor);
      }
    },
  });

  const [{ labelIsDragging, columnIsDragging }, drag] = useDrag({
    item: { accessor },
    type: 'column',
    canDrag: () => !notDraggable,
    collect: (monitor) => ({
      labelIsDragging: monitor.isDragging(),
      columnIsDragging: monitor.getItem()?.accessor === accessor,
    }),
  });

  const connectDragDropRef = (node: HTMLDivElement | null) => {
    if (node) {
      drag(drop(node));
    }
  };

  useEffect(() => {
    onDraggingStateChange?.({ accessor, isDragging: columnIsDragging });
  }, [accessor, columnIsDragging, onDraggingStateChange]);

  return (
    <HeaderCell className={hasFilters ? 'include-filters' : ''} {...rest}>
      <div
        className={twMerge(
          'flex h-full items-center w-full px-4 py-2',
          onDrop && !notDraggable && 'cursor-grab',
          labelIsDragging && 'cursor-move!',
          onDrop && columnIsDragging && 'rs-dragged',
        )}
        ref={connectDragDropRef}
        {...getTestProps(testId, `draggable-${accessor}`)}
      >
        {children}
      </div>
    </HeaderCell>
  );
};

export default DraggableHeaderCell;
